import type { ScanRecognitionRuntimeTelemetry, ScanRecognitionTiming } from "../types/scanWorkflow";
import { currentDeviceName, getDeviceId, isInstalledPwa } from "./deviceService";
import type { VisionRuntimeSnapshot } from "./visualLabService";

const activeRecognitionRuns = new Map<string, number>();

type OcrRuntimeState = "cold" | "starting" | "warm";

type NavigatorRuntimeDetails = Navigator & {
  deviceMemory?: number;
  gpu?: unknown;
  standalone?: boolean;
  userAgentData?: {
    platform?: string;
  };
};

export interface BeginRecognitionRunOptions {
  visionRuntimeSnapshot?: VisionRuntimeSnapshot | null;
  detectionWorkerReady: boolean;
  ocrRuntimeState: OcrRuntimeState;
}

function matchedVersion(userAgent: string, expressions: RegExp[]): string | null {
  for (const expression of expressions) {
    const match = expression.exec(userAgent);
    if (match?.[1]) return match[1];
  }
  return null;
}

function browserIdentity(userAgent: string): ScanRecognitionRuntimeTelemetry["browser"] {
  const isIos = /iPhone|iPad|iPod/i.test(userAgent);
  if (/SamsungBrowser\//i.test(userAgent)) {
    const version = matchedVersion(userAgent, [/SamsungBrowser\/([\d.]+)/i]);
    return { family: "samsung-internet", version, majorVersion: version?.split(".")[0] ?? null, engine: "blink", userAgent };
  }
  if (/Edg(?:A|iOS)?\//i.test(userAgent)) {
    const version = matchedVersion(userAgent, [/EdgA\/([\d.]+)/i, /EdgiOS\/([\d.]+)/i, /Edg\/([\d.]+)/i]);
    return { family: "edge", version, majorVersion: version?.split(".")[0] ?? null, engine: isIos ? "webkit" : "blink", userAgent };
  }
  if (/FxiOS\//i.test(userAgent)) {
    const version = matchedVersion(userAgent, [/FxiOS\/([\d.]+)/i]);
    return { family: "firefox", version, majorVersion: version?.split(".")[0] ?? null, engine: "webkit", userAgent };
  }
  if (/Firefox\//i.test(userAgent)) {
    const version = matchedVersion(userAgent, [/Firefox\/([\d.]+)/i]);
    return { family: "firefox", version, majorVersion: version?.split(".")[0] ?? null, engine: "gecko", userAgent };
  }
  if (/CriOS\//i.test(userAgent)) {
    const version = matchedVersion(userAgent, [/CriOS\/([\d.]+)/i]);
    return { family: "chrome", version, majorVersion: version?.split(".")[0] ?? null, engine: "webkit", userAgent };
  }
  if (/Chrome\//i.test(userAgent) || /Chromium\//i.test(userAgent)) {
    const version = matchedVersion(userAgent, [/Chrome\/([\d.]+)/i, /Chromium\/([\d.]+)/i]);
    return { family: "chrome", version, majorVersion: version?.split(".")[0] ?? null, engine: "blink", userAgent };
  }
  if (/Safari\//i.test(userAgent)) {
    const version = matchedVersion(userAgent, [/Version\/([\d.]+)/i]);
    return { family: "safari", version, majorVersion: version?.split(".")[0] ?? null, engine: "webkit", userAgent };
  }
  return { family: "other", version: null, majorVersion: null, engine: "unknown", userAgent };
}

function deviceClass(userAgent: string): ScanRecognitionRuntimeTelemetry["device"]["deviceClass"] {
  if (/iPhone|iPod/i.test(userAgent)) return "iphone";
  if (/iPad/i.test(userAgent)) return "ipad";
  if (/Android/i.test(userAgent)) return /Mobile/i.test(userAgent) ? "android-phone" : "android-tablet";
  if (/Windows/i.test(userAgent)) return "windows";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "mac";
  return "other";
}

function captureRuntimeTelemetry(
  snapshot: VisionRuntimeSnapshot,
  detectionWorkerReady: boolean,
  ocrRuntimeState: OcrRuntimeState,
): ScanRecognitionRuntimeTelemetry {
  const runtimeNavigator = navigator as NavigatorRuntimeDetails;
  const userAgent = navigator.userAgent || "";
  const deviceMemory = typeof runtimeNavigator.deviceMemory === "number" && Number.isFinite(runtimeNavigator.deviceMemory)
    ? runtimeNavigator.deviceMemory
    : null;
  const logicalCpuCount = Number.isFinite(navigator.hardwareConcurrency) && navigator.hardwareConcurrency > 0
    ? navigator.hardwareConcurrency
    : null;
  return {
    schemaVersion: 1,
    contractVersion: "device-browser-runtime-telemetry-v1",
    capturedAtUtc: new Date().toISOString(),
    device: {
      instanceId: getDeviceId(),
      label: currentDeviceName(),
      deviceClass: deviceClass(userAgent),
      platform: runtimeNavigator.userAgentData?.platform || navigator.platform || null,
      logicalCpuCount,
      deviceMemoryGb: deviceMemory,
      installedPwa: isInstalledPwa(),
      screenWidth: Number.isFinite(window.screen?.width) ? window.screen.width : null,
      screenHeight: Number.isFinite(window.screen?.height) ? window.screen.height : null,
      devicePixelRatio: Number.isFinite(window.devicePixelRatio) ? window.devicePixelRatio : null,
    },
    browser: browserIdentity(userAgent),
    runtime: {
      runtimeApi: snapshot.runtimeApi,
      productionPipelineSha256: snapshot.productionPipelineSha256,
      bundleId: snapshot.bundleId,
      bundleVersion: snapshot.bundleVersion,
      recognitionRevision: snapshot.recognitionRevision,
      detectionBackend: "opencv-js-dedicated-worker",
      detectionWorkerState: detectionWorkerReady ? "warm" : "cold",
      ocrBackend: snapshot.ocr.runtime,
      ocrPreprocessRuntime: snapshot.ocr.preprocessRuntime,
      ocrWorkerState: ocrRuntimeState,
      cnnEnabled: snapshot.cnn.enabled,
      cnnRuntime: snapshot.cnn.runtime,
      webGpuAvailable: Boolean(runtimeNavigator.gpu),
      crossOriginIsolated: globalThis.crossOriginIsolated === true,
      sharedArrayBufferAvailable: typeof SharedArrayBuffer !== "undefined",
      wasmAvailable: typeof WebAssembly !== "undefined",
    },
  };
}

export function beginRecognitionRun(options: BeginRecognitionRunOptions): ScanRecognitionTiming {
  const recognitionRunId = crypto.randomUUID();
  const shutterAtUtc = new Date().toISOString();
  // The monotonic start is captured before telemetry collection so instrumentation
  // overhead is never hidden outside the canonical Shutter→Step2 duration.
  activeRecognitionRuns.set(recognitionRunId, performance.now());
  const runtimeTemperature = options.detectionWorkerReady && options.ocrRuntimeState === "warm" ? "warm" : "cold";
  const runtimeTemperatureBasis = `detection-worker:${options.detectionWorkerReady ? "warm" : "cold"};ocr-worker:${options.ocrRuntimeState}`;
  return {
    schemaVersion: 1,
    contractVersion: "shutter-step2-latency-v1",
    recognitionRunId,
    shutterAtUtc,
    step2DataReadyAtUtc: null,
    shutterToStep2DataReadyMs: null,
    durationClock: "performance.now",
    runtimeTemperature,
    runtimeTemperatureBasis,
    runtimeTelemetry: options.visionRuntimeSnapshot
      ? captureRuntimeTelemetry(
          options.visionRuntimeSnapshot,
          options.detectionWorkerReady,
          options.ocrRuntimeState,
        )
      : null,
  };
}

// SR17: Cold Capture darf die Shutter-Markierung nicht auf einen noch laufenden
// Runtime-Prewarm warten lassen. Die monotone Messung startet deshalb sofort;
// der lokal eingefrorene Runtime-Snapshot kann direkt nach dem Standbild ergänzt
// werden, ohne recognitionRunId/Shutter-Zeit oder Runtime-Temperatur umzuschreiben.
export function attachRecognitionRuntimeSnapshot(
  timing: ScanRecognitionTiming,
  snapshot: VisionRuntimeSnapshot,
  detectionWorkerReadyAtShutter: boolean,
  ocrRuntimeStateAtShutter: OcrRuntimeState,
): ScanRecognitionTiming {
  if (timing.runtimeTelemetry) return timing;
  return {
    ...timing,
    runtimeTelemetry: captureRuntimeTelemetry(
      snapshot,
      detectionWorkerReadyAtShutter,
      ocrRuntimeStateAtShutter,
    ),
  };
}

export function completeRecognitionRun(timing: ScanRecognitionTiming): ScanRecognitionTiming {
  if (timing.step2DataReadyAtUtc && timing.shutterToStep2DataReadyMs !== null) {
    return timing;
  }
  const step2DataReadyAtUtc = new Date().toISOString();
  const startedAt = activeRecognitionRuns.get(timing.recognitionRunId);
  activeRecognitionRuns.delete(timing.recognitionRunId);
  return {
    ...timing,
    step2DataReadyAtUtc,
    // A wall clock must never be used as a duration fallback. Missing monotonic
    // state therefore stays null instead of manufacturing a benchmark value.
    shutterToStep2DataReadyMs: startedAt === undefined
      ? null
      : Math.max(0, Math.round((performance.now() - startedAt) * 10) / 10),
  };
}

export function cancelRecognitionRun(recognitionRunId: string | null | undefined): void {
  if (recognitionRunId) activeRecognitionRuns.delete(recognitionRunId);
}
